import { FC, useCallback, useMemo } from 'react'
import {
  cardPaymentsSelector,
  cardUpcomingPaymentsSelector,
  creditCardErrorsSelector,
  deleteAutoPaymentRequestSelector,
  deletePaymentRequestSelector,
} from 'components/features/creditCard/creditCardStore/selectors'
import { CreditCardPanePreset } from 'components/features/creditCard/creditCardPanes/CreditCardPanePreset'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { ccPaymentsGuard } from 'components/features/creditCard/creditCardStore/guards/ccPaymentsGuard'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { CANCEL_BUTTON_PROPS, KillAllPanesButton, PayCardAmountPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import FloatingPaneNotification from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification'
import { ResetDeleteCCAutoPaymentAction, ResetDeleteCCPaymentAction } from 'components/features/creditCard/creditCardStore/actions'
import CardPaymentItem from 'components/features/creditCard/creditCardPanes/paneComponents/CardPaymentItem'
import Flex from 'components/atoms/Flex/Flex'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { capitalizeFirstLetter } from 'helpers/formatHelpers'
import Separator from 'components/atoms/Separator/Separator'
import moment from 'moment'
import { DATE_FORMAT_READABLE } from 'constants/datetime'

const CardPaymentsPane: FC<{}> = () => {
  const deletePaymentRequest = useStoreSelector(deletePaymentRequestSelector)
  const deleteAutoPaymentRequest = useStoreSelector(deleteAutoPaymentRequestSelector)
  const isLoading = deleteAutoPaymentRequest.isLoading || deletePaymentRequest.isLoading
  const isLoaded = useSimpleGuard(ccPaymentsGuard, {}, 'CreditCardCardPaymentsPane')
  const oldPayments = useStoreSelector(cardPaymentsSelector)
  const upcomingPayments = useStoreSelector(cardUpcomingPaymentsSelector)
  const creditCardErrors = useStoreSelector(creditCardErrorsSelector)
  const dispatch = useStoreDispatch()
  const handleResetCCRequests = useCallback(() => {
    dispatch(new ResetDeleteCCPaymentAction())
    dispatch(new ResetDeleteCCAutoPaymentAction())
  }, [dispatch])

  const errorChild = useMemo(() => creditCardErrors?.length > 0 && (
    <FloatingPaneNotification
      messages={ creditCardErrors }
      onDismiss={ handleResetCCRequests }
      positionAbsolute
    >
      { creditCardErrors }
    </FloatingPaneNotification>
  ), [creditCardErrors, handleResetCCRequests])

  const zeroStateChild = useMemo(() => (
    <>
      <Flex key='zeroState' component='li' theme='card' padding='defaultSize' rounded>
        <p style={{ textAlign: 'center' }}>You have no upcoming payments.</p>
        <PayCardAmountPaneButton text='Make a payment now' fullWidth />
      </Flex>
    </>
  ), [])

  const upcomingPaymentsChild = useMemo(() => {
    if (!isLoaded) {
      return <LoadingOverlay />
    }

    if (!upcomingPayments) {
      return zeroStateChild
    }

    return upcomingPayments?.map((upcomingPayment, idx) => (
      <li key={ idx }>
        <CardPaymentItem { ...upcomingPayment } />
      </li>
    ))
  }, [isLoaded, upcomingPayments, zeroStateChild])

  const pastPaymentsChild = useMemo(() => oldPayments && (
    <Flex component='ul'gap='small'>
      <Separator title='Payment History' large />
      {
        oldPayments?.map((oldPayment, idx) => (
          <Flex component='article' theme='card' padding='defaultSize' rounded key={ idx } gap='none'>
            <Flex component='p' horizontal gap='spread'>
              <label>{ moment(oldPayment.date).format(DATE_FORMAT_READABLE) }</label>
              <strong>{ formatUSCurrency(Math.abs(oldPayment.amount)) }</strong>
            </Flex>
            <Flex horizontal gap='spread'>
              { !!oldPayment.category.value && <small>{ capitalizeFirstLetter(oldPayment.category.value) }</small> }
              { !!oldPayment && <small>{ capitalizeFirstLetter(oldPayment.status) }</small> }
            </Flex>
          </Flex>
        ))
      }
    </Flex>
  ), [oldPayments])

  return (
    <CreditCardPanePreset
      title='Payments'
      notification={ errorChild }
      isLoading={ isLoading }
      tertiaryButton={ <KillAllPanesButton { ...CANCEL_BUTTON_PROPS } text='Close' /> }
      multiCardLayout
    >
      <Flex component='ul' marginBottom='defaultSize'>
        <Separator title='Upcoming Payment(s)' theme='cyan' large />
        { upcomingPaymentsChild }
        <small style={{ textAlign: 'center' }}>
          Cancelations must be submitted by 6PM PT of the previous business day before the next scheduled payment date.
        </small>
      </Flex>
      { pastPaymentsChild }
    </CreditCardPanePreset>
  )
}

export default CardPaymentsPane
